import * as React from 'react';
import { Image } from '@uncinc/uncinc-react-kitchen-sink';
import { MimeType } from '@uncinc/uncinc-react-kitchen-sink/dist/components/MediaPlayer/types';

import {
  API_URL,
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
  SCREEN_WIDE,
} from '../../config/constants';
import { MediaReferenceField } from '../../types/uncinc_profile/helpers/media';
import MediaPlayerSuspense from '../MediaPlayer';
import parseEmbedUrl from '../../helpers/parseEmbedUrl';
import PDFReader from '../PDFReader';

export type AspectRatio = '1_1' | '1_2' | '1_3' | '2_1' | '2_3' | '3_1' | '3_2' | '3_4' | '4_3' | '16_9' | 'original';
export type Breakpoints = 'xs' | 'sm' | 'md' | 'xl';

interface DynamicMediaItemProps {
  media: MediaReferenceField<'svg' | 'video' | 'image' | 'remote_video' | 'document' | 'audio'>;
  imageAspectRatio?: AspectRatio;
  aboveTheFold?: Boolean;
}

const DynamicMediaItem: React.FC<DynamicMediaItemProps> = (props) => {
  const {
    media,
    imageAspectRatio = 'original',
    aboveTheFold = false,
  } = props;

  switch (media.bundle) {
    case 'svg': {
      return <img src={media.source} alt="" />;
    }

    case 'document': {
      return (
        <PDFReader
          // When deeloping locally, there's a fair chance you'll run into CORS errors
          // You can then use this url, for example:
          // url="https://cdn.filestackcontent.com/wcrjf9qPTCKXV3hMXDwK"
          url={media.source}
        />
      );
    }

    case 'audio':
    case 'remote_video':
    case 'video': {
      let type;

      if (media.bundle === 'remote_video') {
        type = parseEmbedUrl(media.embed).mime;
      } else {
        type = media.mime_type;
      }

      return (
        <>
          <MediaPlayerSuspense
            src={media.bundle === 'remote_video' ? media.embed : media.source}
            type={type as MimeType}
          />
          {media.bundle === 'video' && (
            media.entity?.field_caption[0] && (
              <p className="caption pt-3 mb-0">{media.entity.field_caption[0].value}</p>
            )
          )}
        </>
      );
    }

    case 'image': {
      const caption = media.entity?.field_caption?.[0]?.value;
      const breakpoints: Breakpoints[] = ['xs', 'sm', 'md', 'xl'];

      let src;
      if (aboveTheFold) {
        // For images above the fold, adaptive gives poor LCP score,
        // so fall back to a Picture component instead.
        const sizes = {
          xs: SCREEN_MOBILE,
          sm: SCREEN_TABLET,
          md: SCREEN_NORMAL,
          xl: SCREEN_WIDE,
        };

        src = breakpoints.reduce((accumulator, breakpoint) => {
          const imageStyle = media.styles?.[`ratio_${imageAspectRatio}_${breakpoint}`];

          // @ts-ignore
          accumulator[sizes[breakpoint]] = `${API_URL}${imageStyle || media.source}`;

          return accumulator;
        }, {});
      } else {
        src = breakpoints.map((breakpoint) => {
          const imageStyle = media.styles?.[`ratio_${imageAspectRatio}_${breakpoint}`];
          return `${API_URL}${imageStyle || media.source}`;
        });
      }

      return (
        <Image
          src={src}
          alt={caption || media.title}
          lazyload={false}
        />
      );
    }

    default:
      return null;
  }
};

export default DynamicMediaItem;
